import { useScrollOverflow } from './useScrollOverflow';

interface TableScrollIndicators {
  showLeftIndicator: boolean;
  showRightIndicator: boolean;
  showStickyColumnShadow: boolean;
  hasOverflow: boolean;
  scrollPosition: {
    left: number;
    width: number;
    clientWidth: number;
  };
}

/**
 * Custom hook specifically for table scroll indicators
 * Determines when to show left/right gradients and sticky column shadows
 * @param containerRef - React ref to the table container element
 * @param enabled - Whether to enable scroll detection (default: true)
 * @returns Object with boolean flags for showing various scroll indicators
 */
export const useTableScrollIndicators = (
  containerRef: React.RefObject<HTMLElement | null>,
  enabled = true,
): TableScrollIndicators => {
  const scrollState = useScrollOverflow(containerRef, 2); // 2px threshold for sensitivity

  // Don't show any indicators if not enabled
  if (!enabled) {
    return {
      showLeftIndicator: false,
      showRightIndicator: false,
      showStickyColumnShadow: false,
      hasOverflow: false,
      scrollPosition: {
        left: 0,
        width: 0,
        clientWidth: 0,
      },
    };
  }

  return {
    // Show left indicator when scrolled from start
    showLeftIndicator: scrollState.hasOverflow && scrollState.isScrolledLeft,

    // Show right indicator when there's overflow and not scrolled to end
    showRightIndicator: scrollState.hasOverflow && !scrollState.isScrolledRight,

    // Show sticky column shadow when scrolled at all
    showStickyColumnShadow: scrollState.scrollLeft > 0,

    // Expose overflow state
    hasOverflow: scrollState.hasOverflow,

    // Expose scroll position for debugging or advanced use
    scrollPosition: {
      left: scrollState.scrollLeft,
      width: scrollState.scrollWidth,
      clientWidth: scrollState.clientWidth,
    },
  };
};
